"use client";

import { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";

const OTPContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "center",
  alignItems: "center",
}));

const OTPTextField = styled(TextField)(({ theme }) => ({
  width: 56,
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius,
    padding: 0,
  },
  "& input": {
    textAlign: "center",
    fontSize: "1.25rem",
    padding: "8px",
  },
}));

type OTPInputProps = {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
  isNumberInput?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
};

export default function OTPInput({
  length = 6,
  value,
  onChange,
  autoFocus = true,
  isNumberInput = true,
  disabled = false,
  error = "",
  placeholder = "",
}: OTPInputProps) {
  const [internalValue, setInternalValue] = useState<string[]>(() =>
    Array.from({ length }, () => ""),
  );

  const inputsRef = useRef<Array<HTMLInputElement | null>>(
    Array.from({ length }, () => null),
  );

  useEffect(() => {
    if (typeof value === "string") {
      const chars = value.split("").slice(0, length);
      const filled = Array.from({ length }, (_, i) => chars[i] ?? "");
      setInternalValue(filled);
    }
  }, [value, length]);

  const notifyChange = (next: string[]) => {
    onChange?.(next.join(""));
  };

  const setDigit = (index: number, char: string) => {
    const next = [...internalValue];
    next[index] = char;
    if (value === undefined) setInternalValue(next);
    notifyChange(next);
  };

  const focusInput = (index: number) => {
    const input = inputsRef.current[index];
    if (input) input.focus();
  };

  const handleOnChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      let nextChar = raw;
      if (isNumberInput) nextChar = raw.replace(/[^0-9]/g, "");

      if (nextChar.length > 1) {
        const allowed = nextChar.split("").slice(0, length - index);
        allowed.forEach((ch, i) => {
          setDigit(index + i, ch);
        });
        const nextIndex = Math.min(length - 1, index + allowed.length - 1);
        focusInput(nextIndex + 1 < length ? nextIndex + 1 : nextIndex);
        return;
      }

      setDigit(index, nextChar);
      if (nextChar !== "" && index < length - 1) {
        focusInput(index + 1);
      }
    };

  const handleKeyDown =
    (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      const k = e.key;
      if (k === "Backspace") {
        e.preventDefault();
        if (internalValue[index]) {
          setDigit(index, "");
        } else if (index > 0) {
          setDigit(index - 1, "");
          focusInput(index - 1);
        }
      } else if (k === "ArrowLeft" && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      } else if (k === "ArrowRight" && index < length - 1) {
        e.preventDefault();
        focusInput(index + 1);
      } else if (k === " " || k === "Spacebar") {
        e.preventDefault();
      }
    };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("Text");
    const filtered = isNumberInput ? paste.replace(/[^0-9]/g, "") : paste;
    if (!filtered) return;

    const chars = filtered.split("").slice(0, length);
    const next = Array.from(
      { length },
      (_, i) => chars[i] ?? internalValue[i] ?? "",
    );
    if (value === undefined) setInternalValue(next);
    notifyChange(next);

    const lastFilled = Math.min(length - 1, chars.length - 1);
    focusInput(lastFilled + 1 < length ? lastFilled + 1 : lastFilled);
  };

  return (
    <>
      <OTPContainer>
        {Array.from({ length }).map((_, i) => (
          <OTPTextField
            key={i}
            error={!!error}
            slotProps={{
              input: {
                inputMode: isNumberInput ? "numeric" : "text",
                "aria-label": `OTP digit ${i + 1}`,
                placeholder,
                sx: {
                  textAlign: "center",
                  fontSize: "1.25rem",
                  padding: "8px",
                },
              },
            }}
            value={internalValue[i] ?? ""}
            onChange={handleOnChange(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={handlePaste}
            size="small"
            disabled={disabled}
            inputRef={(el) => {
              inputsRef.current[i] = el;
              if (i === 0 && autoFocus && el) {
                const shouldFocus =
                  typeof value === "undefined"
                    ? !internalValue[0]
                    : (value?.length ?? 0) === 0;
                if (shouldFocus) {
                  setTimeout(() => el.focus(), 0);
                }
              }
            }}
          />
        ))}
      </OTPContainer>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </>
  );
}
